/**
 * Helper: Search
 * -----------------------------------------------------------------------------
 * - Provides shared helpers for normalising search terms and generating
 *   equivalent query variants.
 * - Keeps predictive search and full search results aligned when separator
 *   characters affect Shopify search matching.
 */

/**
 * Maximum length of a code-like word fragment. (e.g. 'cr', 'v')
 */
const MAX_MERGEABLE_WORD_LENGTH = 3

/**
 * Maximum trailing code length for compact token splitting.
 */
const MAX_NESTED_CODE_LENGTH = 4

/**
 * Maximum length of a compact trailing word to split.
 */
const MAX_NESTED_WORD_LENGTH = 8

/**
 * Minimum prefix length for compact token splitting.
 */
const MIN_NESTED_PREFIX_LENGTH = 3

/**
 * Remove separators from a search query.
 * @param {String} searchQuery - Raw search input.
 * @returns {String} Compacted query with separators removed.
 */
export function getCompactSearchQuery(searchQuery) {
  return (searchQuery || '').trim().replace(/[\s\-_]+/g, '')
}

/**
 * Check whether a compact query supports fuzzy matching.
 * @param {String} compactQuery - Compacted search query.
 * @returns {Boolean}
 */
export function isFuzzySearchCandidate(compactQuery) {
  return compactQuery.length >= 2 &&
    compactQuery.length <= 8 &&
    (/^[a-z0-9]+$/i).test(compactQuery)
}

/**
 * Extract the trailing fuzzy term group from a search query.
 * @param {String} searchQuery - Raw search input.
 * @returns {Object|null} Prefix words, suffix words, and compact suffix.
 */
function getFuzzyWordGroup(searchQuery) {
  const words = (searchQuery || '').trim().split(/\s+/).filter(Boolean)

  if (words.length === 0) {
    return null
  }

  let startIndex = words.length - 1

  // Merge adjacent short fragments while the combined value
  // remains fuzzy-matchable.
  while (startIndex > 0) {
    const precedingWord = words[startIndex - 1]

    if (precedingWord.length > MAX_MERGEABLE_WORD_LENGTH) {
      break
    }

    const candidateWords = words.slice(startIndex - 1)
    const candidateCompact = getCompactSearchQuery(candidateWords.join(' '))

    if (!isFuzzySearchCandidate(candidateCompact)) {
      break
    }

    startIndex -= 1
  }

  const suffixWords = words.slice(startIndex)
  const suffixCompact = getCompactSearchQuery(suffixWords.join(' '))

  if (!isFuzzySearchCandidate(suffixCompact)) {
    return null
  }

  return {
    prefixWords: words.slice(0, startIndex),
    suffixWords,
    suffixCompact,
  }
}

/**
 * Create variants for a compact prefix and trailing code.
 * @param {String} compact - Compact alphanumeric string.
 * @returns {Set<String>} Variants including the original compact string.
 */
function buildDashVariants(compact) {
  const variants = new Set([compact])

  for (let position = 1; position < compact.length; position += 1) {
    variants.add(`${compact.slice(0, position)}-${compact.slice(position)}`)
  }

  return variants
}

/**
 * Create variants for a compact token split into a prefix and suffix.
 * @param {String} token - Compact search token.
 * @returns {Array<String>} Query variants.
 */
function getNestedTokenVariants(token) {
  const variants = new Set()
  const maxWordLength = Math.min(
    MAX_NESTED_WORD_LENGTH,
    token.length - MIN_NESTED_PREFIX_LENGTH,
  )

  for (let wordLength = 2; wordLength <= maxWordLength; wordLength += 1) {
    const prefix = token.slice(0, token.length - wordLength)
    const suffix = token.slice(token.length - wordLength)

    variants.add(`${prefix} ${suffix}`)

    if (wordLength <= MAX_NESTED_CODE_LENGTH) {
      buildDashVariants(suffix).forEach((variant) => {
        variants.add(`${prefix} ${variant}`)
      })
    }
  }

  return Array.from(variants)
}

/**
 * Normalise a search query for form submission.
 * @param {String} searchQuery - Raw search input.
 * @returns {String} Normalised search query.
 */
export function normalizeSearchQuery(searchQuery) {
  const query = (searchQuery || '').trim()
  const group = getFuzzyWordGroup(query)

  if (!group) {
    return query
  }

  const { prefixWords, suffixCompact } = group

  return prefixWords.length
    ? `${prefixWords.join(' ')} ${suffixCompact}`
    : suffixCompact
}

/**
 * Build unique query variants for predictive and full search.
 * @param {String} searchQuery - Raw search input.
 * @returns {Array<String>} List of query variants to run in parallel.
 */
export function getSearchQueryVariants(searchQuery) {
  const query = (searchQuery || '').trim()
  const group = getFuzzyWordGroup(query)

  if (!group) {
    const compactQuery = getCompactSearchQuery(query)

    if (!(/\s/).test(query) && /^[a-z0-9]+$/i.test(compactQuery)) {
      return Array.from(new Set([
        query,
        ...getNestedTokenVariants(compactQuery),
      ]))
    }

    return [query]
  }

  const { prefixWords, suffixWords, suffixCompact } = group
  const prefix = prefixWords.length ? `${prefixWords.join(' ')} ` : ''

  const suffixVariants = new Set([suffixCompact, suffixWords.join(' ')])

  for (let position = 1; position < suffixCompact.length; position += 1) {
    suffixVariants.add(
      `${suffixCompact.slice(0, position)}-${suffixCompact.slice(position)}`,
    )
  }

  const queries = new Set(
    Array.from(suffixVariants).map((variant) => `${prefix}${variant}`),
  )

  // Recover missing word boundaries in compact queries.
  if (!prefixWords.length && suffixWords.length === 1) {
    getNestedTokenVariants(suffixCompact).forEach((variant) => {
      queries.add(variant)
    })
  }

  // Always include the untouched raw query as a fallback variant.
  queries.add(query)

  return Array.from(queries)
}